import React, { useEffect } from 'react';
import { Table, Row, Col, FormGroup, Label, Button, Badge } from 'reactstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useRowSelect,
} from 'react-table';

import Select from 'react-select';
import { Icon } from '@assets';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input
          type='checkbox'
          className='custom-checkbox'
          ref={resolvedRef}
          {...rest}
        />
      </>
    );
  }
);

function GlobalFilter({
  preGlobalFilteredRows: _preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);

  const onChange = useAsyncDebounce((val) => {
    setGlobalFilter(val || undefined);
  }, 200);

  return (
    <div className='search-bar d-inline-block inline-block w-100'>
      <FormGroup className='mb-0 w-auto d-inline-block'>
        <div className='d-flex align-items-center w-100'>
          <Label
            for='invoice-search'
            className='mb-0 w-auto d-inline-flex align-items-center mr-2'
          >
            <Icon icon='search' />
          </Label>
          <input
            id='invoice-search'
            value={value || ''}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
            placeholder='Search by keywords'
            className='w-100'
          />
        </div>
      </FormGroup>
    </div>
  );
}

const mobileStyles = {
  filterContainer: {
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '16px',
    background: '#f8fafc',
    boxShadow: '0 6px 18px rgba(15, 34, 58, 0.08)',
  },
  summaryRow: {
    display: 'grid',
    gap: '10px',
    marginBottom: '12px',
  },
  summaryCard: {
    borderRadius: '14px',
    padding: '12px',
    background: '#ffffff',
    boxShadow: '0 8px 20px rgba(15, 34, 58, 0.08)',
  },
  filterHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#475569',
  },
  filterLabel: {
    fontWeight: 600,
  },
  actionRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },
  actionButton: {
    flex: '1 1 160px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 14px',
    borderRadius: '12px',
    boxShadow: '0 6px 16px rgba(15, 34, 58, 0.12)',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingBottom: '72px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 10px 28px rgba(15, 34, 58, 0.1)',
    border: '1px solid #eef2f7',
    cursor: 'pointer',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 auto',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2933',
  },
  statusBadge: {
    fontSize: '13px',
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: '999px',
    textTransform: 'capitalize',
  },
  detailSection: {
    marginTop: '12px',
    display: 'grid',
    rowGap: '8px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '13px',
    color: '#334155',
    lineHeight: 1.45,
  },
  actionButtonsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '16px',
  },
  actionPrimary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
  actionSecondary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
  paginationRow: {
    justifyContent: 'center',
    width: '100%',
  },
};

const statusColors = {
  drafted: '#94a3b8',
  sent: '#2563eb',
  followed_up: '#f59e0b',
  accepted: '#0ea5e9',
  paid: '#16a34a',
};

export default function InvoiceTable({ columns, data, addons, changeIsChecked, setSidebarState, onFilteredDataChange, summary, invoiceStatuses }) {
  var totalRecord = data.length;
  const pageSizeOptions = [
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '75', label: '75' },
    { value: '100', label: '100' },
    { value: totalRecord.toString(), label: 'All' },
  ];
  const defaultColumn = React.useMemo(
    () => ({
      width: 'auto',
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    rows,
    state: { pageIndex, globalFilter, sortBy },
    prepareRow,
  } = useTable(
    { columns, data, defaultColumn, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) =>
        changeIsChecked
          ? [
              // Let's make a column for selection
              {
                id: 'selection',
                Header: ({ getToggleAllPageRowsSelectedProps }) => (
                  <IndeterminateCheckbox
                    {...getToggleAllPageRowsSelectedProps()}
                  />
                ),
                Cell: ({ row }) => (
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} onClick={(e) => e.stopPropagation()}/>
                ),
                width: 50,
                maxWidth: 50,
              },
              ...columns,
            ]
          : columns
      );
    }
  );

  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(rows.map(r => r.original));
    }
  }, [rows]);

  const selectedRowSignature = React.useMemo(
    () =>
      selectedFlatRows
        .map((row) => row.id || row.original?.node?.id || '')
        .join('|'),
    [selectedFlatRows]
  );

  useEffect(() => {
    if (changeIsChecked) {
      changeIsChecked(selectedFlatRows);
    }
  }, [selectedRowSignature, changeIsChecked]);

	const history = useHistory();
  const desktopAddons = React.Children.toArray(addons);
  const mobileActionButtons = desktopAddons
    .map((child, index) => {
      if (!React.isValidElement(child)) return null;
      const cleanedClasses = (child.props.className || '')
        .split(' ')
        .filter((cls) => cls !== 'float-right' && cls !== 'mr-2' && cls !== 'mt-sm-0')
        .join(' ')
        .trim();
      return React.cloneElement(child, {
        key: index,
        className: `${cleanedClasses} mobile-action-btn`.trim(),
        style: { ...mobileStyles.actionButton, ...(child.props.style || {}) },
      });
    })
    .filter(Boolean);

  const sortLabel = React.useMemo(() => {
    if (!sortBy || !sortBy.length) return 'Default';
    const flatHeaders = headerGroups.flatMap((group) => group.headers);
    const match = flatHeaders.find((col) => col.id === sortBy[0].id);
    if (!match) return 'Custom';
    const headerValue = match.Header;
    if (typeof headerValue === 'string') return headerValue;
    if (typeof headerValue === 'function') return 'Custom';
    if (React.isValidElement(headerValue)) return 'Custom';
    return `${match.id}`.replace(/_/g, ' ');
  }, [sortBy, headerGroups]);

  return (
    <>
      <div className='d-none d-sm-block'>
        <Row className='my-4 d-flex align-items-center'>
          <Col sm='6'>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          </Col>

          <Col sm='6' className='text-lg-right'>
            {addons}
          </Col>
        </Row>

        <Table
          responsive
          className='client-list text-left min-w-900'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps([
                      {
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      },
                      column.getSortByToggleProps(),
                    ])}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <>
                            &nbsp; <Icon icon='sort-down' size='lg' />
                          </>
                        ) : (
                          <>
                            &nbsp; <Icon icon='sort-up' size='lg' />
                          </>
                        )
                      ) : column.canSort ? (
                        <>
                          &nbsp; <Icon icon='sort' size='lg' />
                        </>
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => {
                    setSidebarState(true);
                    history.push(`/invoices/show/${row.original.node.id}`);
                  }}
                >
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
            {page.length <= 3 ? (
              <tr>
                <div className='spacer-div'></div>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      <div className='d-block d-sm-none'>
        <div style={mobileStyles.filterContainer}>
          <div style={mobileStyles.filterHeader}>
            <div onClick={(e) => e.stopPropagation()}>
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </div>
            {summary ? (
              <div style={mobileStyles.summaryRow}>
                <div style={mobileStyles.summaryCard}>
                  <div className='text-muted small'>Total Paid Amount</div>
                  <div className='h6 mb-0 text-success'>
                    ${summary.paidAmount.toLocaleString()}
                  </div>
                </div>
                <div style={mobileStyles.summaryCard}>
                  <div className='text-muted small'>Total Outstanding Amount</div>
                  <div className='h6 mb-0 text-danger'>
                    ${summary.outstandingAmount.toLocaleString()}
                  </div>
                </div>
                <div style={mobileStyles.summaryCard}>
                  <div className='text-muted small'>Invoice Status Count</div>
                  <div className='h6 mb-0 text-danger'>
                    {Object.entries(summary.countByStatus).map(([status, count]) => {
                      const statusData = invoiceStatuses.find(s => s.value === status);
                      return (
                        <Badge color={statusData?.color || 'dark'} key={status} className='mr-2'>
                          {statusData?.label || status}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
            <div style={mobileStyles.filterRow}>
              <span style={mobileStyles.filterLabel}>{`${rows.length} Invoice${rows.length === 1 ? '' : 's'}`}</span>
              <span>Sort By: {sortLabel}</span>
            </div>
          </div>
          <div style={mobileStyles.actionRow}>{mobileActionButtons}</div>
        </div>

        <div style={mobileStyles.listWrapper}>
          {page.map((row) => {
            prepareRow(row);
            const node = row.original?.node || {};
            const statusColor = statusColors[node.status] || '#6b7280';
            const selectionProps = row.getToggleRowSelectedProps();
            const statusCell = row.cells.find((cell) => cell.column.Header === 'Status');
            const actionCell = row.cells.find((cell) => cell.column.Header === 'Action');

            return (
              <div
                key={row.id}
                style={mobileStyles.card}
                onClick={() => {
                  setSidebarState(true);
                  history.push(`/invoices/show/${node.id}`);
                }}
              >
                <div style={mobileStyles.headerRow}>
                  <div style={mobileStyles.headerLeft}>
                    <div style={mobileStyles.nameRow}>
                      <div onClick={(e) => e.stopPropagation()}>
                        <IndeterminateCheckbox {...selectionProps} />
                      </div>
                      <span style={mobileStyles.title}>{node.client?.name || 'Invoice'}</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '13px' }}>
                      {node.property?.name || 'No property selected'}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                      #{node.invoiceNumber || '—'}
                    </div>
                  </div>
                  <div>
                    <span
                      style={{
                        ...mobileStyles.statusBadge,
                        color: statusColor,
                        background: `${statusColor}15`,
                      }}
                    >
                      {(node.status || 'drafted').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div style={mobileStyles.detailSection}>
                  <div>
                    <div style={mobileStyles.detailLabel}>Lease Amount</div>
                    <div style={mobileStyles.detailValue}>
                      {node.leaseAmount
                        ? `$${Number(node.leaseAmount).toLocaleString()}`
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>Created On</div>
                    <div style={mobileStyles.detailValue}>
                      {node.createdAt ? moment(node.createdAt).format('ll') : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>Last Updated</div>
                    <div style={mobileStyles.detailValue}>
                      {node.updatedAt ? moment(node.updatedAt).fromNow() : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>Agent</div>
                    <div style={mobileStyles.detailValue}>
                      {node.user?.name || '-'}
                    </div>
                  </div>
                  {statusCell ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div style={mobileStyles.detailLabel}>Update Status</div>
                      <div style={mobileStyles.detailValue}>{statusCell.render('Cell')}</div>
                    </div>
                  ) : null}
                </div>

                {actionCell ? (
                  <div
                    style={mobileStyles.actionButtonsRow}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actionCell.render('Cell')}
                  </div>
                ) : (
                  <div
                    style={mobileStyles.actionButtonsRow}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      color='primary'
                      style={mobileStyles.actionPrimary}
                      onClick={() => {
                        setSidebarState(true);
                        history.push(`/invoices/show/${node.id}`);
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      color='secondary'
                      style={mobileStyles.actionSecondary}
                      onClick={() => {
                        setSidebarState(true);
                        history.push(`/invoices/edit/${node.id}`);
                      }}
                      disabled={node.status === 'paid'}
                    >
                      Edit
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Row className='d-flex align-items-center mt-4 flex-column flex-md-row'>
        <Col
          md='6'
          className='d-flex justify-content-md-start justify-content-center'
        >
          <FormGroup className='mb-3 mb-md-0'>
            <Label
              for='invoice-page-size'
              className='mb-0 w-auto d-inline-block mr-2 mr-md-3'
            >
              Showing Entries
            </Label>
            <Select
              inputId='invoice-page-size'
              name='PageSize'
              defaultValue={pageSizeOptions[0]}
              options={pageSizeOptions}
              onChange={(e) => {
                setPageSize(Number(e.value));
              }}
              className='basic-multi-select text-left'
              classNamePrefix='default-select'
              menuPlacement={'auto'}
            />
          </FormGroup>
        </Col>

        <Col
          md='6'
          className='d-flex justify-content-center mt-2 mt-md-0'
        >
          <ul className='pagination'>
            <li onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
                <Icon icon='chevron-left' />
              </a>
            </li>
            <li onClick={() => previousPage()} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
              </a>
            </li>
            <span className='d-flex align-items-center px-3'>
              Page &nbsp; <strong>{pageIndex + 1}</strong> &nbsp; of &nbsp;
              <strong>{pageOptions.length}</strong>
            </span>
            <li onClick={() => nextPage()} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
              </a>
            </li>
            <li onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
                <Icon icon='chevron-right' />
              </a>
            </li>
          </ul>
        </Col>
      </Row>
    </>
  );
}
